import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { GetOptions, Preferences, RemoveOptions } from '@capacitor/preferences';

export interface StorageItem<T> {
    data: T;
    lastUpdated: number;
}

@Injectable({
    providedIn: 'root'
})
export class HybridStorageService {
    private isReady = false;
    public memory: Record<string, string> = {};

    get ready(): Promise<void> {
        return this.populateMemory();
    }

    async setItem<T>(key: string, value: T, asCookie = false): Promise<void> {

        try {
            const storageItem: StorageItem<T> = {
                data: value,
                lastUpdated: Date.now()
            };
            const serialized = JSON.stringify(storageItem);
            this.memory[key] = serialized;

            if (Capacitor.isNativePlatform()) {
                await this.setCapacitorItem(key, serialized);
            } else if (asCookie) {
                this.setCookieItem(key, serialized);
            } else {
                this.setBrowserItem(key, serialized);
            }
        } catch (error) {
            console.error('HybridStorage setItem failed for:', key, error);
            throw error;
        }
    }

    private async setCapacitorItem(key: string, value: string): Promise<void> {
        try {
            await Preferences.set({ key, value });
        } catch (err) {
            console.error('Error saving to Capacitor Storage:', key, err);
            throw err;
        }
    }

    private setCookieItem(key: string, value: string): void {
        try {
            document.cookie = `${key}=${value}; path=/`;
        } catch (error) {
            console.error('Error saving to cookies:', error);
        }
    }

    private setBrowserItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.error('Error saving to localStorage:', error);
        }
    }

    async getItem<T>(key: string, asCookie: boolean = false): Promise<T | null> {
        const storageItem = await this.getStorageItem<T>(key, asCookie);
        return storageItem?.data ?? null;
    }

    async getStorageItem<T>(key: string, asCookie: boolean = false): Promise<StorageItem<T> | null> {
        if (Capacitor.isNativePlatform()) {
            return await this.getCapacitorItem<T>(key);
        } else if (asCookie) {
            return this.getCookieItem<T>(key);
        } else {
            return this.getBrowserItem<T>(key);
        }
    }

    async getItemWithTimestamp<T>(key: string, asCookie: boolean = false): Promise<{ data: T | null, lastUpdated: number | null }> {
        const storageItem = await this.getStorageItem<T>(key, asCookie);
        return {
            data: storageItem?.data ?? null,
            lastUpdated: storageItem?.lastUpdated ?? null
        };
    }

    async getItemIfFresh<T>(key: string, maxAgeMinutes: number, asCookie: boolean = false): Promise<T | null> {
        const storageItem = await this.getStorageItem<T>(key, asCookie);

        if (!storageItem) {
            return null;
        }

        const maxAgeMs = maxAgeMinutes * 60 * 1000;
        const age = Date.now() - storageItem.lastUpdated;

        if (age > maxAgeMs) {
            // Item is stale, remove it
            await this.removeItem(key);
            return null;
        }

        return storageItem.data;
    }

    getItemFromMemory(key: string): string | null {
        const value = this.memory[key];
        return value ? this.safeParse<string>(value) : null;
    }

    private async getCapacitorItem<T>(key: string): Promise<StorageItem<T> | null> {
        try {
            let options: GetOptions = { key: key };
            const { value } = await Preferences.get(options);
            return value ? this.safeParse<StorageItem<T>>(value) : null;
        } catch (error) {
            console.error('Error reading from Capacitor Storage:', error);
            return null;
        }
    }

    private getCookieItem<T>(key: string): StorageItem<T> | null {
        try {
            const match = `; ${document.cookie}`.match(`;\\s*${key}=([^;]+)`);
            if (match) {
                return this.safeParse<StorageItem<T>>(decodeURIComponent(match[1]));
            }
        } catch (error) {
            console.error('Error reading from cookies:', error);
        }
        return null;
    }

    private getBrowserItem<T>(key: string): StorageItem<T> | null {
        try {
            const item = localStorage.getItem(key);
            return item ? this.safeParse<StorageItem<T>>(item) : null;
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return null;
        }
    }

    async removeItem(key: string): Promise<void> {
        try {
            await this.removeCapacitorItem(key);
            this.removeBrowserItem(key);
            delete this.memory[key];
        } catch (error) {
            console.error('Error removing item from storage:', error);
        }
    }
    
    private removeCapacitorItem = async (key: string) => {
        try {
            let options: RemoveOptions = { key: key };
            await Preferences.remove(options);
        } catch (error) {
            console.error('Error removing from Capacitor Storage:', error);
        }
    }

    private removeBrowserItem(key: string): void {
        try {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        } catch (error) {
            console.error('Error removing from localStorage:', error);
        }    
    }

    async clear(): Promise<void> {
        try {
            localStorage.clear();
            await Preferences.clear();
            this.memory = {};
            document.cookie = "";
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    }

    async clearStorageForImpersonation(): Promise<void> {
        const keysToClear = HybridStorageKeys.impersonateStorageKeys();
        const allKeys = await this.getAllKeys();

        for (const key of keysToClear) {
            // Check if this is a prefix key (ends with underscore)
            if (key.endsWith('_')) {
                // Remove all keys that start with this prefix
                const keysToRemove = allKeys.filter(k => k.startsWith(key));
                for (const keyToRemove of keysToRemove) {
                    await this.removeItem(keyToRemove);
                }
            } else {
                // Remove the exact key
                await this.removeItem(key);
            }
        }
    }

    async hasItem(key: string): Promise<boolean> {
        if (Capacitor.isNativePlatform()) {
            return await Preferences.get({ key }).then(result => result.value !== null);
        } else {
            return localStorage.getItem(key) !== null;
        }
    }

    async getAllKeys(): Promise<string[]> {
        if (Capacitor.isNativePlatform()) {
            return (await Preferences.keys()).keys;
        } else {
            return Object.keys(localStorage);
        }
    }

    private async populateMemory(): Promise<void> {
        if (this.isReady) return;

        try {
            const keys = await this.getAllKeys();
        for (const key of keys) {
            const result = await this.getItem<string>(key);
            if (result !== null) {
                this.memory[key] = result;
            }
        }
        this.isReady = true;
        } catch (err) {
        console.error('Failed to load tokens from Preferences:', err);
        }
    }

    private safeParse<T>(s: string): T | null {
        try { return JSON.parse(s) as T; } catch { return null; }
    }
}

export enum HybridStorageKeys {
  // Capacitor
  Capacitor_DeepLinkPath = 'deepLinkPath',
  Capacitor_backgroundTimestamp = 'lastBackgroundTimestamp',
  Capacitor_stateChangeIsInitialized = 'stateChangeIsInitialized',
  // Demo Mode
  DemoMode = 'demoMode',
  // Okta
  // Okta_CacheStorage = 'okta-cache-storage',
  // Okta_LoginType = 'okta_loginType',
  // Okta_accessToken = 'accessToken',
  // Okta_refreshToken = 'refreshToken',
  // Main Menu
  MainMenu_UserFirstName = 'mainMenu_userFirstName',
  // _v2: payloads cached before header apps shipped have no HeaderIconClass/HeaderSortOrder,
  // so an unbumped key would render a Home-only nav row for up to 30 minutes after deploy.
  MainMenu_MenuItems = 'mainMenu_menuItems_v2',
  MainMenu_ParentPortalStudents = 'mainMenu_parentPortalStudents',
  // A logged-in student's own ParentPortal record (drives their College Readiness tile)
  MainMenu_SelfPortalStudent = 'mainMenu_selfPortalStudent',
  // School Meals / Website / Care / Tour — per-school public CMS data, so not wiped on impersonation
  SchoolMeals_MenuUrlPrefix = 'schoolMeals_menuUrl_',
  SchoolWebsite_AliasMap = 'schoolWebsite_aliasMap',
  SchoolCare_HasCareFlagPrefix = 'schoolCare_hasBeforeAfter_',
  SchoolTour_LinkPrefix = 'schoolTour_link_',
  // User Images
  UserImages_PreloaderPatterns = 'UserImages_preloaderPatterns', 
      // TODO look for other "'image_preloader_patterns'" and change, but also like do we need this? 
  // Messaging
  Messaging_messageTextCachePrefix = 'messageTextCache_',
  Messaging_directMessages = 'messaging_directMessages',
  Messaging_posts = 'messaging_posts',
  Messaging_archivedDirectMessages = 'messaging_archivedDirectMessages',
  Messaging_archivedPosts = 'messaging_archivedPosts',
  Messaging_searchedDirectMessages = 'messaging_searchedDirectMessages',
  Messaging_searchedPosts = 'messaging_searchedPosts',
  Messaging_notifications = 'messaging_notifications',
  Messaging_collapsedPosts = 'messaging_collapsedPosts',
  Messaging_collapsedIndividualGroupChats = 'messaging_collapsedIndividualGroupChats',
  Messaging_selectedListSection = 'messaging_selectedListSection',
  Messaging_searchString = 'messaging_searchString',
  Messaging_isShowingArchived = 'messaging_isShowingArchived',
  Messaging_isShowingSearchResults = 'messaging_isShowingSearchResults',
  Messaging_hasDirectMessages = 'messaging_hasDirectMessages',
  Messaging_hasPosts = 'messaging_hasPosts',
  Messaging_RawChatListDto = 'messaging_rawChatListDto',
  // PTM
  PTM_Location = 'ptm_location',
  PTM_AcademicYear = 'ptm_academicYear',
  PTM_BackSource = 'ptm_backSource',
  PTM_DSQId = 'ptm_dsqId',
  // SPARK
  Spark_ShowFavorites = 'spark_showFavorites',
  Spark_ShowAvatars = 'spark_showAvatars',
  Spark_DontShowGroupInstructions = 'spark_dontShowGroupInstructions',
  Spark_SchoolRewardsPointValue = 'spark_schoolRewardsPointValue',
  Spark_TimerDuration = 'spark_timerDuration',
  // User Service
  UserService_CurrentUserProfile = 'UserService_userProfile',
  UserService_IsImpersonating = 'UserService_isImpersonating',
  UserService_OriginalUserProfile = 'UserService_originalUserProfile',
  UserService_UserUseAvatar = 'UserService_userUseAvatar_',
  UserService_UserImageResponse = 'UserService_userImageResponse_',
  // Login Debug
  LoginDebug_Enabled = 'LoginDebug_enabled',
  // Directory
  Directory_SelectedSchool = 'Directory_selectedSchool',
  Directory_Users = 'Directory_users_',
  Directory_schools = 'Directory_schools',
  Directory_SearchBox = 'Directory_searchBox_',
  Directory_RoleFilter = 'Directory_roleFilter_',
  Directory_FilterDirection = 'Directory_filterDirection_',
  // Groups
  Group_SelectedFilter = 'Group_selectedFilter',
  // Posts
  Post_FeedExpandAllToggle = 'Post_FeedExpandAllToggle',
  Post_FeedSearchString = 'Post_FeedSearchString',
  Post_FeedSelectedFilter = 'Post_FeedSelectedFilter',
  Post_FeedSidebarOpen = 'Post_FeedSidebarOpen',
  Post_FeedMobileTab = 'Post_FeedMobileTab',
  Post_FeedSelectedGroupId = 'Post_FeedSelectedGroupId',
  Post_FeedSelectedTagId = 'Post_FeedSelectedTagId',
  Post_FeedSelectedAddOnFilter = 'Post_FeedSelectedAddOnFilter',
  Post_FeedSelectedSchoolIds = 'Post_FeedSelectedSchoolIds',
  Post_DividerPreferences = 'Post_DividerPreferences',
  Post_StyledSectionPreferences = 'Post_StyledSectionPreferences',
  Post_ShowAdvancedEditorTools = 'Post_ShowAdvancedEditorTools',
  Post_EditorHeight = 'Post_EditorHeight',
  Post_TemplateManagerFilter = 'Post_TemplateManagerFilter',
  Post_TemplateManagerSort = 'Post_TemplateManagerSort',
  // Translation
  Translation_Language = 'app-language',
  Translation_LanguageId = 'app-language-id',
  // AI Chat Widget
  AiChat_TypedMessages = 'ai-typed-chat-messages',
  AiChat_VoiceMessages = 'ai-voice-chat-messages',
  AiChat_WidgetPosition = 'ai-widget-position',
  Post_AddonTemplateManagerFilter = 'Post_AddonTemplateManagerFilter',
  Post_AddonTemplateManagerSort = 'Post_AddonTemplateManagerSort',
  Post_FullTemplateManagerFilter = 'Post_FullTemplateManagerFilter',
  Post_FullTemplateManagerSort = 'Post_FullTemplateManagerSort',
  // Grades
  Grades_SortBy = 'Grades_sortBy',
  Grades_SortDirection = 'Grades_sortDirection',
  Grades_GradingScaleCollapsed = 'Grades_gradingScaleCollapsed',
  Grades_ProficiencyLegendCollapsed = 'Grades_proficiencyLegendCollapsed',
  Grades_ParticipationLegendCollapsed = 'Grades_participationLegendCollapsed',
  Grades_AssignmentSortBy = 'Grades_assignmentSortBy',
  Grades_AssignmentSortDirection = 'Grades_assignmentSortDirection',
  Grades_AssignmentTypeBadge = 'Grades_AssignmentTypeBadge',
  Grades_ShowClassSchedule = 'Grades_ShowClassSchedule',
  Grades_ShowStudent = 'Grades_ShowStudent',
  Grades_NeedsAttentionOnTop = 'Grades_NeedsAttentionOnTop',
  Grades_NeedsAttentionHidden = 'Grades_needsAttentionHidden_',
  Grades_ViewMode = 'Grades_viewMode',
  Grades_SelectedAcademicYear = 'Grades_selectedAcademicYear',
  Post_DesignTemplateManagerFilter = 'Post_DesignTemplateManagerFilter',
  Post_DesignTemplateManagerSort = 'Post_DesignTemplateManagerSort',
}

// Anything that is user specific should be cleared out when impersonating
export namespace HybridStorageKeys {
    export function impersonateStorageKeys(): string[] {
        return [
            HybridStorageKeys.MainMenu_UserFirstName,
            HybridStorageKeys.MainMenu_MenuItems,
            HybridStorageKeys.MainMenu_ParentPortalStudents,
            HybridStorageKeys.MainMenu_SelfPortalStudent,
            HybridStorageKeys.Messaging_messageTextCachePrefix,
            HybridStorageKeys.PTM_Location,
            HybridStorageKeys.PTM_AcademicYear,
            HybridStorageKeys.PTM_BackSource,
            HybridStorageKeys.PTM_DSQId,
            HybridStorageKeys.Spark_ShowFavorites,
            HybridStorageKeys.Spark_ShowAvatars,
            HybridStorageKeys.Spark_DontShowGroupInstructions,
            HybridStorageKeys.UserService_CurrentUserProfile,
            HybridStorageKeys.UserService_UserUseAvatar,
            HybridStorageKeys.Directory_SelectedSchool,
            HybridStorageKeys.Directory_Users,
            HybridStorageKeys.Directory_schools,
            HybridStorageKeys.Directory_SearchBox,
            HybridStorageKeys.Directory_RoleFilter,
            HybridStorageKeys.Directory_FilterDirection,
            HybridStorageKeys.UserService_UserImageResponse,
            HybridStorageKeys.Group_SelectedFilter,
            HybridStorageKeys.Post_FeedExpandAllToggle,
            HybridStorageKeys.Post_FeedSearchString,
            HybridStorageKeys.Post_FeedSelectedFilter,
            HybridStorageKeys.Post_FeedSelectedGroupId,
            HybridStorageKeys.Post_FeedSelectedTagId,
            HybridStorageKeys.Post_FeedSelectedSchoolIds,
            HybridStorageKeys.Post_TemplateManagerFilter,
            HybridStorageKeys.Post_TemplateManagerSort,
            HybridStorageKeys.Post_AddonTemplateManagerFilter,
            HybridStorageKeys.Post_AddonTemplateManagerSort,
            HybridStorageKeys.Post_FullTemplateManagerFilter,
            HybridStorageKeys.Post_FullTemplateManagerSort,
            HybridStorageKeys.Post_DesignTemplateManagerFilter,
            HybridStorageKeys.Post_DesignTemplateManagerSort,
            HybridStorageKeys.Messaging_directMessages,
            HybridStorageKeys.Messaging_posts,
            HybridStorageKeys.Messaging_notifications,
            HybridStorageKeys.Messaging_archivedDirectMessages,
            HybridStorageKeys.Messaging_archivedPosts,
            HybridStorageKeys.Messaging_searchString,
            HybridStorageKeys.Messaging_isShowingArchived,
            HybridStorageKeys.Messaging_isShowingSearchResults,
            HybridStorageKeys.Messaging_searchedDirectMessages,
            HybridStorageKeys.Messaging_searchedPosts,
            HybridStorageKeys.Messaging_hasDirectMessages,
            HybridStorageKeys.Messaging_hasPosts,
            HybridStorageKeys.Messaging_RawChatListDto,
            HybridStorageKeys.Grades_NeedsAttentionHidden,
            // AI chat history is per-user — wipe it so an impersonated session starts clean.
            HybridStorageKeys.AiChat_TypedMessages,
            HybridStorageKeys.AiChat_VoiceMessages,
        ];
    }
}
